import {
  ExportOutlined,
  FileSearchOutlined,
  RedoOutlined,
  SearchOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import { Button, Col, ConfigProvider, Form, Row, Tooltip } from "antd";
import { ColumnsType } from "antd/es/table";
import { defaultAppTransactionRepository } from "api/repositories/appTransaction/AppTransactionRepository";
import { AppTransactionResponseModel } from "api/repositories/appTransaction/models/AppTransactionResponseModel";
import { UserRoles } from "api/repositories/authentications/model/LoginResponseModel";
import CardComponent from "components/generalComponents/cardComponent/CardComponent";
import { FilterButtons } from "components/generalComponents/filterButtons";
import { FilterComponent } from "components/generalComponents/filterComponents/FilterComponents";
import popUpComponent from "components/generalComponents/popUpComponent/PopUpComponent";
import TableComponent from "components/generalComponents/tableComponent/TableComponent";
import AppTransactionDetailModal from "components/modals/AppTransactionDetailModal";
import { AuthenticationContext } from "context/AuthenticationContext";
import dayjs from "dayjs";
import { useEffect } from "react";
import { colorFormat } from "utils/format/ColorFormat";
import { strings } from "utils/localizedStrings";
import AppPhoneRechargeViewModel from "../viewModel/AppPhoneRechargeViewModel";
import { AppPhoneRechargeConstants } from "./constants";

const AppPhoneRechargeView = () => {
  const {
    filterForm,
    loading,
    detailLoading,
    transList,
    transDetail,
    resultObject,
    page,
    setPage,
    limit,
    total,
    setQuery,
    showModal,
    setShowModal,
    onDetailModalClose,
    setSelectedItem,
    exportReportTransaction,
    handleTableChange,
    service,
    onCheckStatus,
    checkStatusLoading,
  } = AppPhoneRechargeViewModel(defaultAppTransactionRepository);
  const { user } = AuthenticationContext();

  useEffect(() => {
    if (!!resultObject?.message) {
      switch (resultObject?.type) {
        case "success":
          popUpComponent.success(resultObject?.message, 5);
          break;
        case "error":
          popUpComponent.error(resultObject?.message, 5);
          break;
        default:
          break;
      }
    }
  }, [resultObject]);

  const columns: ColumnsType<AppTransactionResponseModel> = [
    ...AppPhoneRechargeConstants?.columns,
    {
      title: strings.GlobalLabels.Detail,
      key: "detail",
      align: "center",
      width: "10%",
      fixed: "right",
      render: (_, record) => (
        <div className="flex flex-row gap-x-2 items-center justify-center">
          <Tooltip placement="top" title={strings.GlobalLabels.Detail}>
            <Button
              type="primary"
              ghost
              shape="circle"
              onClick={() => {
                setSelectedItem(record?.code);
                setShowModal(true);
              }}
              icon={<FileSearchOutlined />}
            />
          </Tooltip>
          {user?.role === UserRoles.admin && (
            <ConfigProvider
              theme={{ token: { colorPrimary: colorFormat.Purple } }}
            >
              <Tooltip
                placement="top"
                title={strings.TransactionHistory.StatusCheck}
              >
                <Button
                  type="primary"
                  ghost
                  shape="circle"
                  loading={checkStatusLoading?.[record?.code]}
                  disabled={
                    Object.values(checkStatusLoading).some(Boolean) &&
                    !checkStatusLoading?.[record?.code]
                  }
                  onClick={() => {
                    onCheckStatus(record?.code);
                  }}
                  icon={<SyncOutlined />}
                />
              </Tooltip>
            </ConfigProvider>
          )}
        </div>
      ),
    },
  ];

  const handleExportReport = () => {
    const values = filterForm.getFieldsValue();
    const payload = {
      fromDate: values?.date
        ? dayjs(values?.date[0]).format("YYYY-MM-DD")
        : dayjs().startOf("month").format("YYYY-MM-DD"),
      toDate: values?.date
        ? dayjs(values?.date[1]).format("YYYY-MM-DD")
        : dayjs().format("YYYY-MM-DD"),
      status: values?.status,
      code: values?.code,
      transRef: values?.transRef,
      type: values?.type,
      note: values?.note,
      name: values?.name,
      page: 0,
      limit: 10,
      service,
    };
    // Remove fields that are undefined
    const filteredPayload = Object.fromEntries(
      Object.entries(payload).filter(([_, v]) => v !== undefined)
    );
    exportReportTransaction(filteredPayload);
  };

  return (
    <CardComponent
      data={{
        children: (
          <>
            <Form
              form={filterForm}
              onFinish={(values) =>
                setQuery({
                  fromDate: values?.date
                    ? dayjs(values?.date[0]).format("YYYY-MM-DD")
                    : dayjs().startOf("month").format("YYYY-MM-DD"),
                  toDate: values?.date
                    ? dayjs(values?.date[1]).format("YYYY-MM-DD")
                    : dayjs().format("YYYY-MM-DD"),
                  status: values?.status === "" ? undefined : values?.status,
                  code: values?.code === "" ? undefined : values?.code,
                  transRef:
                    values?.transRef === "" ? undefined : values?.transRef,
                  type: values?.type === "" ? undefined : values?.type,
                  note: values?.note === "" ? undefined : values?.note,
                  name: values?.name === "" ? undefined : values?.name,
                  page: 0,
                  limit: limit,
                  service,
                })
              }
            >
              <Row gutter={[6, 0]} align={"middle"}>
                {/* Filters */}
                {FilterComponent(AppPhoneRechargeConstants?.filterAttributes)}
                {/* Buttons */}
                {FilterButtons({
                  searchLg: 8,
                  htmlType: "submit",
                  redoIcon: <RedoOutlined />,
                  redoLg: 8,
                  searchIcon: <SearchOutlined />,
                  redoName: strings.GlobalLabels.Redo,
                  searchName: strings.GlobalLabels.Search,
                  type: "primary",
                  onRedoClick: async () => {
                    setPage(0);
                    filterForm.resetFields();
                    setQuery({
                      page: 0,
                      limit: 10,
                      service,
                      fromDate: dayjs().startOf("month").format("YYYY-MM-DD"),
                      toDate: dayjs().format("YYYY-MM-DD"),
                    });
                  },
                })}
                <Col span={24} lg={8}>
                  <Form.Item>
                    <ConfigProvider
                      theme={{
                        token: {
                          colorPrimary: colorFormat.Purple,
                        },
                      }}
                    >
                      <Button
                        style={{ backgroundColor: colorFormat.Purple }}
                        type="primary"
                        className="w-full"
                        onClick={handleExportReport}
                        icon={<ExportOutlined />}
                      >
                        {strings.AppTransactionHistory.ExportReportTiTle}
                      </Button>
                    </ConfigProvider>
                  </Form.Item>
                </Col>
                {/* Table List */}
                <TableComponent<AppTransactionResponseModel>
                  data={{
                    columns,
                    dataSource: transList,
                    rowKey: "code",
                    handleTableChange,
                    loading,
                    loadingTitle: "...Loading",
                    page,
                    pageSize: limit,
                    total,
                    totalTitle: strings.GlobalLabels.Total,
                    scroll: { x: 1300, y: 900 },
                  }}
                />
              </Row>
            </Form>
            {showModal && (
              <AppTransactionDetailModal
                open={showModal}
                onCancel={onDetailModalClose}
                detail={transDetail}
                loading={detailLoading}
                Constants={AppPhoneRechargeConstants}
              />
            )}
          </>
        ),
        title: strings.AppTransactionHistory.AppPhoneRechargeTitle,
        extra: null,
      }}
    />
  );
};

export default AppPhoneRechargeView;
